import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useGoals } from '../../contexts/GoalContext';
import { useExpenses } from '../../contexts/ExpenseContext';
import { useChecklists } from '../../contexts/ChecklistContext';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useCurrency } from '../../hooks/useCurrency';
import { formatDate, getTodayString } from '../../utils/dateUtils';
import { validateGoalForm } from '../../utils/validationUtils';
import { getReorderedOrder } from '../../utils/reorderUtils';
import { BarChart, Bar, Cell, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CHART_AHEAD, CHART_BEHIND, CHART_GRID, CHART_ZERO } from '../../utils/chartPalette';

const statuses = ['Not Started', 'In Progress', 'Completed', 'Cancelled'];
const statusKeys = { 'Not Started': 'notStarted', 'In Progress': 'inProgress', Completed: 'completed', Cancelled: 'cancelled' };
const emptyForm = { title: '', description: '', startDate: '', endDate: '', targetAmount: '', achievedAmount: '', status: 'Not Started', category: 'activity' };

// The optional attachments a new goal can bring with it. A goal like "make the
// volleyball team" is really a checklist, "save for a car" is really a savings budget,
// and "eat healthy" can be both or neither - so both are opt-in and independent rather
// than being implied by the goal's money/activity type.
const emptyAttachments = {
  withChecklist: false,
  checklistReminderType: 'daily',
  checklistReminderTime: '',
  checklistTasks: [],
  withSaving: false,
  savingAmount: '',
};

const statusIcons = { 'Not Started': '⏳', 'In Progress': '🚀', Completed: '🏆', Cancelled: '✕' };

// A deadline inside a week is the one people can still act on; further out it is a
// date, not a decision. Everything on the page that says "needs attention" uses this.
const SOON_DAYS = 7;
const FILTER_KEYS = ['all', 'attention', 'active', 'completed', 'cancelled'];

function statusSlug(status) {
  return status.toLowerCase().replace(/\s+/g, '-');
}

// endDate is a plain "YYYY-MM-DD" string in the schema, but slice defensively so a
// full ISO timestamp from an older row can't turn every date into NaN.
function toISODate(value) {
  return String(value || '').slice(0, 10);
}

function daysUntil(isoDate, today) {
  const from = new Date(`${today}T00:00:00`);
  const to = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(to.getTime())) return null;
  return Math.round((to - from) / 86400000);
}

function isFinished(goal) {
  return goal.status === 'Completed' || goal.status === 'Cancelled';
}

function goalProgressPercent(goal) {
  return goal.targetAmount
    ? Math.min(100, Math.round((goal.achievedAmount / goal.targetAmount) * 100))
    : Math.min(100, Math.round(((new Date() - new Date(goal.startDate)) / (new Date(goal.endDate) - new Date(goal.startDate))) * 100));
}

// One derived word per goal - the card stripe, the due badge and the filters all read
// from it, so they can never disagree about whether a goal is late.
function goalTone(goal, today) {
  if (goal.status === 'Completed') return 'done';
  if (goal.status === 'Cancelled') return 'cancelled';
  const days = daysUntil(toISODate(goal.endDate), today);
  if (days === null) return 'active';
  if (days < 0) return 'overdue';
  if (days <= SOON_DAYS) return 'soon';
  return 'active';
}

function dueFlag(goal, today, t) {
  const isoDate = toISODate(goal.endDate);
  const days = daysUntil(isoDate, today);
  if (isFinished(goal) || days === null) return { label: t('due', { date: formatDate(isoDate) }), tone: '' };
  if (days < 0) {
    const late = -days;
    return { label: late === 1 ? t('dueFlag.oneDayOverdue') : t('dueFlag.daysOverdue', { count: late }), tone: 'danger' };
  }
  if (days === 0) return { label: t('dueFlag.today'), tone: 'danger' };
  if (days === 1) return { label: t('dueFlag.oneDayLeft'), tone: 'warn' };
  if (days <= SOON_DAYS) return { label: t('dueFlag.daysLeft', { count: days }), tone: 'warn' };
  return { label: t('due', { date: formatDate(isoDate) }), tone: '' };
}

function matchesFilter(goal, tone, filterKey) {
  if (filterKey === 'all') return true;
  if (filterKey === 'attention') return tone === 'overdue' || tone === 'soon';
  if (filterKey === 'active') return !isFinished(goal);
  if (filterKey === 'completed') return goal.status === 'Completed';
  return goal.status === 'Cancelled';
}

// How much of a goal's time window has already gone. The pace chart compares this
// with real progress; on its own it is not shown anywhere, because "60% of the time has
// passed" only means something next to how far along you actually are.
function timeElapsedPercent(goal, today) {
  const start = new Date(`${toISODate(goal.startDate)}T00:00:00`).getTime();
  const end = new Date(`${toISODate(goal.endDate)}T00:00:00`).getTime();
  const now = new Date(`${today}T00:00:00`).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
  return Math.min(100, Math.max(0, Math.round(((now - start) / (end - start)) * 100)));
}

function PaceTooltip({ active, payload, t }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <div className="chart-tooltip">
      <strong>{row.name}</strong>
      <span>{t('charts.progressLine', { progress: row.progress, elapsed: row.elapsed })}</span>
      <span className={row.delta < 0 ? 'negative' : ''}>
        {row.delta === 0
          ? t('charts.onPace')
          : row.delta > 0
            ? t('charts.aheadBy', { points: row.delta })
            : t('charts.behindBy', { points: Math.abs(row.delta) })}
      </span>
    </div>
  );
}

function GoalCard({
  goal, tone, progress, category, isBudgetLinked, isChecklistLinked,
  statusLabels, t, formatCurrency, handleAddToSavings, handleCreateChecklist,
  openProgressModal, handleEdit, handleDelete, sortable, today,
}) {
  const dragStyle = sortable ? { transform: CSS.Transform.toString(sortable.transform), transition: sortable.transition } : undefined;
  const flag = dueFlag(goal, today, t);
  const hasTarget = Number(goal.targetAmount) > 0;
  const amounts = category === 'money'
    ? `${formatCurrency(goal.achievedAmount)} / ${formatCurrency(goal.targetAmount)}`
    : `${goal.achievedAmount} / ${goal.targetAmount}`;

  return (
    <article
      ref={sortable?.setNodeRef}
      style={dragStyle}
      {...(sortable?.attributes || {})}
      {...(sortable?.listeners || {})}
      className={`goal-card is-${tone}${sortable ? ' draggable' : ''}${sortable?.isDragging ? ' dragging' : ''}`}
    >
      <div className="goal-head">
        <span className="goal-title-group">
          <span className="goal-type-icon" aria-hidden="true">{category === 'money' ? '💰' : '🎯'}</span>
          <h3 title={goal.title}>{goal.title}</h3>
        </span>
        <span className={`goal-due-flag ${flag.tone}`}>{flag.label}</span>
      </div>

      {goal.description && <p className="goal-description">{goal.description}</p>}

      <div className="goal-progress">
        <div className="goal-progress-head">
          {/* Without a target the percentage is time elapsed, which is worth saying
              out loud - otherwise the number looks like progress nobody made. */}
          <span className="goal-progress-value">{hasTarget ? amounts : t('progressByTime')}</span>
          <span className="goal-progress-percent">{progress}%</span>
        </div>
        <div className="progress-bar"><div style={{ width: `${progress}%` }} /></div>
      </div>

      <div className="goal-tag-row">
        <span className={`status-pill ${statusSlug(goal.status)}`}>{statusIcons[goal.status]} {statusLabels[goal.status]}</span>
        {category === 'money' ? (
          isBudgetLinked ? (
            <Link to="/expenses" className="goal-link-badge money linked">{t('inSavings')}</Link>
          ) : (
            <button type="button" className="goal-link-badge money" onClick={() => handleAddToSavings(goal)}>{t('addToSavings')}</button>
          )
        ) : (
          isChecklistLinked ? (
            <Link to="/checklist" className="goal-link-badge activity linked">{t('checklistReady')}</Link>
          ) : (
            <button type="button" className="goal-link-badge activity" onClick={() => handleCreateChecklist(goal)}>{t('createDailyChecklist')}</button>
          )
        )}
      </div>

      <div className="goal-card-foot">
        <button type="button" onClick={() => openProgressModal(goal)} className="goal-act-btn accent">{t('update')}</button>
        <button type="button" onClick={() => handleEdit(goal)} className="goal-act-btn">{t('edit')}</button>
        <button type="button" onClick={() => handleDelete(goal)} className="goal-act-btn danger">{t('delete')}</button>
      </div>
    </article>
  );
}

function SortableGoalCard(props) {
  const sortable = useSortable({ id: props.goal.id });
  return <GoalCard {...props} sortable={sortable} />;
}

function GoalDashboard() {
  const { t } = useTranslation('goals');
  const { goals, addGoal, updateGoal, deleteGoal, reorderGoal } = useGoals();
  const { budgets, addBudget, updateBudget } = useExpenses();
  const { checklists, addChecklist } = useChecklists();
  const { addToast } = useToast();
  const confirm = useConfirm();
  const { formatCurrency } = useCurrency();
  const statusLabels = useMemo(() => Object.fromEntries(statuses.map((status) => [status, t(`statuses.${statusKeys[status]}`)])), [t]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [search, setSearch] = useState('');
  const [filterKey, setFilterKey] = useState('all');
  const [sortKey, setSortKey] = useState('deadline');
  const [errors, setErrors] = useState({});
  const [attachments, setAttachments] = useState(emptyAttachments);
  const [newTaskText, setNewTaskText] = useState('');
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressGoal, setProgressGoal] = useState(null);
  const [progressValue, setProgressValue] = useState('');

  const today = getTodayString();

  // Progress against the clock. Only goals with a real target can appear: for a goal
  // without one, goalProgressPercent *is* time elapsed, so its bar would be zero by
  // construction and would say nothing at all. Most-behind first, because that is the
  // end of the list worth acting on.
  const paceRows = useMemo(() => goals
    .filter((goal) => !isFinished(goal) && Number(goal.targetAmount) > 0)
    .map((goal) => {
      const elapsed = timeElapsedPercent(goal, today);
      if (elapsed === null) return null;
      const progress = goalProgressPercent(goal);
      return { name: goal.title, progress, elapsed, delta: progress - elapsed };
    })
    .filter(Boolean)
    .sort((a, b) => a.delta - b.delta)
    .slice(0, 8), [goals, today]);

  const tones = useMemo(
    () => new Map(goals.map((goal) => [goal.id, goalTone(goal, today)])),
    [goals, today],
  );

  const counts = useMemo(() => {
    const tally = { all: goals.length, attention: 0, active: 0, completed: 0, cancelled: 0 };
    goals.forEach((goal) => {
      const tone = tones.get(goal.id);
      if (tone === 'overdue' || tone === 'soon') tally.attention += 1;
      if (!isFinished(goal)) tally.active += 1;
      if (goal.status === 'Completed') tally.completed += 1;
      if (goal.status === 'Cancelled') tally.cancelled += 1;
    });
    return tally;
  }, [goals, tones]);

  // The goals that are actually at risk, soonest first - the focus band names the top
  // one so "3 need attention" is a fact the user can act on rather than a number.
  const attentionGoals = useMemo(
    () => goals
      .filter((goal) => ['overdue', 'soon'].includes(tones.get(goal.id)))
      .sort((a, b) => toISODate(a.endDate).localeCompare(toISODate(b.endDate))),
    [goals, tones],
  );
  const overdueCount = useMemo(
    () => goals.filter((goal) => tones.get(goal.id) === 'overdue').length,
    [goals, tones],
  );
  const completionRate = goals.length ? Math.round((counts.completed / goals.length) * 100) : 0;

  const filteredGoals = useMemo(() => {
    const matches = goals.filter((goal) => {
      const matchesSearch = !search || goal.title.toLowerCase().includes(search.toLowerCase());
      return matchesSearch && matchesFilter(goal, tones.get(goal.id), filterKey);
    });
    const sorted = [...matches];
    if (sortKey === 'deadline') {
      // Finished goals keep their place in the list but not at the top of it - a goal
      // completed last March should not head the queue just because its date was early.
      sorted.sort((a, b) => (Number(isFinished(a)) - Number(isFinished(b))) || toISODate(a.endDate).localeCompare(toISODate(b.endDate)));
    } else if (sortKey === 'progress-desc') sorted.sort((a, b) => goalProgressPercent(b) - goalProgressPercent(a));
    else if (sortKey === 'progress-asc') sorted.sort((a, b) => goalProgressPercent(a) - goalProgressPercent(b));
    else if (sortKey === 'recent') sorted.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    // 'custom': keep the array's own order (already sorted by `order` from the API/context).
    return sorted;
  }, [goals, search, filterKey, sortKey, tones]);

  const dragSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { delay: 200, tolerance: 5 } }));

  const handleGoalDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const fromIndex = filteredGoals.findIndex((goal) => goal.id === active.id);
    const toIndex = filteredGoals.findIndex((goal) => goal.id === over.id);
    if (fromIndex === -1 || toIndex === -1) return;
    reorderGoal(active.id, getReorderedOrder(filteredGoals, fromIndex, toIndex));
  };

  const focusHeadline = () => {
    if (overdueCount) return t('focus.overdue');
    if (counts.attention) return t('focus.dueSoon');
    if (counts.active) return t('focus.onTrack');
    return t('focus.allComplete');
  };

  const syncLinkedBudget = (goal, achievedAmount, targetAmount) => {
    if ((goal.category || 'activity') !== 'money' || !goal.linkedBudgetId) return;
    const linkedBudget = budgets.find((item) => item.id === goal.linkedBudgetId);
    if (!linkedBudget) return;
    updateBudget({ ...linkedBudget, amount: targetAmount, actualAmount: achievedAmount });
  };

  const openAddGoal = () => {
    setEditingId(null);
    setForm(emptyForm);
    setAttachments(emptyAttachments);
    setNewTaskText('');
    setErrors({});
    setShowGoalForm(true);
  };

  const closeGoalForm = () => {
    setShowGoalForm(false);
    setEditingId(null);
    setForm(emptyForm);
    setAttachments(emptyAttachments);
    setNewTaskText('');
    setErrors({});
  };

  const clearFilters = () => {
    setFilterKey('all');
    setSearch('');
  };

  const addAttachmentTask = () => {
    const trimmed = newTaskText.trim();
    if (!trimmed) return;
    setAttachments((current) => ({
      ...current,
      // Date.now() would collide when two tasks are added inside the same millisecond,
      // which is easy to do by holding Enter.
      checklistTasks: [...current.checklistTasks, { id: `${Date.now()}-${current.checklistTasks.length}`, text: trimmed, completed: false }],
    }));
    setNewTaskText('');
  };

  const removeAttachmentTask = (index) => {
    setAttachments((current) => ({
      ...current,
      checklistTasks: current.checklistTasks.filter((_, position) => position !== index),
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const validation = validateGoalForm(form);
    if (Object.keys(validation).length) {
      setErrors(validation);
      return;
    }

    const payload = {
      ...form,
      targetAmount: Number(form.targetAmount) || 0,
      achievedAmount: Number(form.achievedAmount) || 0,
    };

    if (editingId) {
      updateGoal({ ...payload, id: editingId });
      syncLinkedBudget(payload, payload.achievedAmount, payload.targetAmount);
      addToast(t('toast.goalUpdated'), 'success');
    } else {
      // The server creates the goal, checklist and budget together and links them, so
      // a half-attached goal can't exist. Attachments are only offered on create -
      // an existing goal already has the "Add to Savings" / "Create checklist" buttons
      // on its card, and duplicating that here would give two ways to make the same
      // link with different results.
      addGoal({
        ...payload,
        checklist: attachments.withChecklist ? {
          reminderType: attachments.checklistReminderType,
          reminderTime: attachments.checklistReminderTime || null,
          date: payload.startDate,
          tasks: attachments.checklistTasks,
        } : null,
        savingBudget: attachments.withSaving ? {
          amount: Number(attachments.savingAmount) || payload.targetAmount,
        } : null,
      });
      addToast(t('toast.goalAdded'), 'success');
    }

    closeGoalForm();
  };

  const handleEdit = (goal) => {
    setEditingId(goal.id);
    setForm({ ...goal, targetAmount: String(goal.targetAmount), achievedAmount: String(goal.achievedAmount), category: goal.category || 'activity' });
    setErrors({});
    setShowGoalForm(true);
  };

  const handleDelete = async (goal) => {
    // Deleting the goal also deletes the budget/checklist it created, so say so up
    // front rather than letting rows disappear from two other pages unannounced.
    const hasLinked = Boolean(goal.linkedBudgetId || goal.linkedChecklistId);
    const message = hasLinked
      ? t('confirmDeleteWithLinked', { title: goal.title })
      : t('confirmDelete', { title: goal.title });
    const confirmed = await confirm(message);
    if (!confirmed) return;
    deleteGoal(goal.id);
    addToast(t('toast.goalDeleted'), 'success');
  };

  const openProgressModal = (goal) => {
    setProgressGoal(goal);
    setProgressValue(String(goal.achievedAmount));
    setShowProgressModal(true);
  };

  const closeProgressModal = () => {
    setShowProgressModal(false);
    setProgressGoal(null);
    setProgressValue('');
  };

  const handleProgressSubmit = (event) => {
    event.preventDefault();
    const nextAchieved = Number(progressValue) || 0;
    // The status is the server's to decide (utils/goalStatus.js): this used to set
    // 'Completed' here and never clear it, so a goal that hit its target once stayed
    // finished even after the amount was corrected back down - and mobile, which has no
    // such rule, showed something different for the same row.
    updateGoal({ ...progressGoal, achievedAmount: nextAchieved });
    syncLinkedBudget(progressGoal, nextAchieved, progressGoal.targetAmount);
    addToast(t('toast.progressUpdated'), 'success');
    closeProgressModal();
  };

  const handleAddToSavings = async (goal) => {
    try {
      const createdBudget = await addBudget({ category: goal.title, amount: goal.targetAmount, actualAmount: goal.achievedAmount, type: 'Saving' });
      await updateGoal({ ...goal, linkedBudgetId: createdBudget.id });
      addToast(t('toast.addedToSavings'), 'success');
    } catch (error) {
      addToast(error.message || t('toast.somethingWrong'), 'error');
    }
  };

  const handleCreateChecklist = async (goal) => {
    try {
      const createdChecklist = await addChecklist({
        title: goal.title,
        description: goal.description || '',
        date: getTodayString(),
        tasks: [{ id: Date.now(), text: goal.title, completed: false }],
      });
      await updateGoal({ ...goal, linkedChecklistId: createdChecklist.id });
      addToast(t('toast.checklistCreated'), 'success');
    } catch (error) {
      addToast(error.message || t('toast.somethingWrong'), 'error');
    }
  };

  useEffect(() => {
    if (!showGoalForm && !showProgressModal) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (showGoalForm) closeGoalForm();
      else if (showProgressModal) closeProgressModal();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showGoalForm, showProgressModal]);

  const progressGoalIsMoney = progressGoal && (progressGoal.category || 'activity') === 'money';
  const formatProgressAmount = (amount) => (progressGoalIsMoney ? formatCurrency(amount) : String(amount));

  const cardProps = (goal) => ({
    goal,
    tone: tones.get(goal.id),
    progress: goalProgressPercent(goal),
    category: goal.category || 'activity',
    isBudgetLinked: !!goal.linkedBudgetId && budgets.some((item) => item.id === goal.linkedBudgetId),
    isChecklistLinked: !!goal.linkedChecklistId && checklists.some((item) => item.id === goal.linkedChecklistId),
    statusLabels,
    t,
    today,
    formatCurrency,
    handleAddToSavings,
    handleCreateChecklist,
    openProgressModal,
    handleEdit,
    handleDelete,
  });

  return (
    <section className="page-card">
      <div className="page-header goals-header">
        <div>
          <p className="eyebrow">{t('eyebrow')}</p>
          <h1 className="display-title">{t('heading')}</h1>
        </div>
        <div className="page-actions">
          <button onClick={openAddGoal} className="primary-btn standout-btn">
            <span aria-hidden="true">+</span> {t('addGoal')}
          </button>
        </div>
      </div>

      {/* The focus band and the pace chart answer the same question from two sides -
          which goals need attention, and whether the set as a whole is keeping up - so
          they share a row instead of each taking a full-width strip of the page. */}
      {goals.length > 0 && (
        <div className={`dash-lead${paceRows.length > 0 ? '' : ' is-single'}`}>
          <section className={`goal-focus${overdueCount ? ' alert' : ''}`}>
            <div className="goal-focus-top">
              <div>
                <p className="goal-focus-eyebrow">{t('focus.eyebrow')}</p>
                <h3 className="goal-focus-headline">{focusHeadline()}</h3>
              </div>
              {counts.attention > 0 && filterKey !== 'attention' && (
                <button type="button" className="text-btn" onClick={() => setFilterKey('attention')}>{t('focus.showThem')}</button>
              )}
            </div>
            <div className="goal-focus-stats">
              <div className="goal-focus-stat">
                <label>{t('focus.activeLabel')}</label>
                <strong>{counts.active}</strong>
                <span className="goal-focus-note">{t('stats.outOfTotal', { total: goals.length })}</span>
              </div>
              <div className="goal-focus-stat">
                <label>{t('stats.completionRate')}</label>
                <strong>{completionRate}%</strong>
                <div className="progress-bar"><div style={{ width: `${completionRate}%` }} /></div>
              </div>
              <div className="goal-focus-stat">
                <label>{t('focus.attentionLabel')}</label>
                <strong className={counts.attention ? 'danger' : ''}>{counts.attention}</strong>
                <span className="goal-focus-note">
                  {attentionGoals.length ? t('focus.soonest', { title: attentionGoals[0].title }) : t('focus.nothingAtRisk')}
                </span>
              </div>
            </div>
          </section>

          {paceRows.length > 0 && (
            <section className="dash-chart-card goal-pace-card">
              <header className="dash-chart-head">
                <h2>{t('charts.paceTitle')}</h2>
                <span className="dash-chart-sub">{t('charts.paceSub')}</span>
              </header>
              <div className="dash-chart-body">
                <ResponsiveContainer width="100%" height={Math.max(140, paceRows.length * 34 + 34)}>
                  <BarChart data={paceRows} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                    <CartesianGrid stroke={CHART_GRID} strokeDasharray="3 3" horizontal={false} />
                    <XAxis
                      type="number"
                      domain={[-100, 100]}
                      ticks={[-100, -50, 0, 50, 100]}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 11, fill: 'currentColor', opacity: 0.55 }}
                      tickFormatter={(value) => `${value > 0 ? '+' : ''}${value}`}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={132}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 12, fill: 'currentColor', opacity: 0.75 }}
                    />
                    <Tooltip cursor={{ fill: 'currentColor', opacity: 0.06 }} content={<PaceTooltip t={t} />} />
                    {/* Neutral, never a hue: the midpoint is an absence of polarity. */}
                    <ReferenceLine x={0} stroke={CHART_ZERO} strokeWidth={2} />
                    <Bar dataKey="delta" radius={4} maxBarSize={18}>
                      {paceRows.map((row) => (
                        <Cell key={row.name} fill={row.delta < 0 ? CHART_BEHIND : CHART_AHEAD} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* Two colours means identity cannot rest on colour alone. */}
              <ul className="dash-chart-legend">
                <li><span className="dash-chart-swatch" style={{ background: CHART_AHEAD }} />{t('charts.legendAhead')}</li>
                <li><span className="dash-chart-swatch" style={{ background: CHART_BEHIND }} />{t('charts.legendBehind')}</li>
              </ul>
            </section>
          )}
        </div>
      )}

      {goals.length > 0 && (
        <div className="goal-toolbar">
          <div className="goal-chips">
            {FILTER_KEYS.map((key) => {
              // Cancelled is rare; showing an always-empty chip costs more than it gives.
              if (key === 'cancelled' && !counts.cancelled && filterKey !== 'cancelled') return null;
              return (
                <button
                  key={key}
                  type="button"
                  className={`goal-chip${key === 'attention' ? ' alert' : ''}${filterKey === key ? ' active' : ''}`}
                  aria-pressed={filterKey === key}
                  onClick={() => setFilterKey(key)}
                >
                  {t(`filters.${key}`)}
                  <span className="goal-chip-count">{counts[key]}</span>
                </button>
              );
            })}
          </div>
          <div className="goal-toolbar-controls">
            <input
              className="goal-search"
              placeholder={t('searchPlaceholder')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <select className="goal-sort" value={sortKey} onChange={(event) => setSortKey(event.target.value)} aria-label={t('sort.label')}>
              <option value="deadline">{t('sort.deadline')}</option>
              <option value="progress-desc">{t('sort.progressDesc')}</option>
              <option value="progress-asc">{t('sort.progressAsc')}</option>
              <option value="recent">{t('sort.recent')}</option>
              <option value="custom">{t('sort.custom')}</option>
            </select>
          </div>
        </div>
      )}

      <div className="goal-grid">
        {goals.length === 0 && (
          <div className="goal-empty">
            <span className="goal-empty-icon" aria-hidden="true">🎯</span>
            <h3>{t('empty.title')}</h3>
            <p>{t('empty.body')}</p>
            <div className="goal-empty-actions">
              <button type="button" className="primary-btn standout-btn" onClick={openAddGoal}>{t('empty.cta')}</button>
            </div>
          </div>
        )}
        {goals.length > 0 && filteredGoals.length === 0 && (
          <div className="goal-empty">
            <span className="goal-empty-icon" aria-hidden="true">🔍</span>
            <h3>{t('empty.noMatchTitle')}</h3>
            <p>{t('empty.noMatchBody')}</p>
            <div className="goal-empty-actions">
              <button type="button" className="text-btn" onClick={clearFilters}>{t('empty.clear')}</button>
              <button type="button" className="text-btn" onClick={openAddGoal}>{t('addGoalCta')}</button>
            </div>
          </div>
        )}
        {sortKey === 'custom' ? (
          <DndContext sensors={dragSensors} collisionDetection={closestCenter} onDragEnd={handleGoalDragEnd}>
            <SortableContext items={filteredGoals.map((goal) => goal.id)} strategy={verticalListSortingStrategy}>
              {filteredGoals.map((goal) => <SortableGoalCard key={goal.id} {...cardProps(goal)} />)}
            </SortableContext>
          </DndContext>
        ) : filteredGoals.map((goal) => <GoalCard key={goal.id} {...cardProps(goal)} />)}
      </div>

      {showGoalForm && (
        <div className="modal-overlay" onClick={closeGoalForm}>
          <form onSubmit={handleSubmit} className="card form-card modal-content goal-form-modal" onClick={(event) => event.stopPropagation()}>
            <h3>{editingId ? t('form.editTitle') : t('form.addTitle')}</h3>
            {!editingId && <p className="modal-subtitle">{t('form.subtitle')}</p>}
            <label>
              {t('form.goalTitle')}
              <input autoFocus value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
              {errors.title && <span className="error-text">{t('validation.titleRequired')}</span>}
            </label>
            <label>
              {t('form.goalType')}
              <div className="segmented-toggle">
                <button type="button" className={form.category === 'money' ? 'active' : ''} onClick={() => setForm({ ...form, category: 'money' })}>{t('form.moneyRelated')}</button>
                <button type="button" className={form.category !== 'money' ? 'active' : ''} onClick={() => setForm({ ...form, category: 'activity' })}>{t('form.dailyActivity')}</button>
              </div>
            </label>
            <label>
              {t('form.description')}
              <textarea value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
            </label>
            <div className="inline-fields">
              <label>
                {t('form.startDate')}
                <input type="date" value={form.startDate} onChange={(event) => setForm({ ...form, startDate: event.target.value })} />
                {errors.startDate && <span className="error-text">{t('validation.startDateRequired')}</span>}
              </label>
              <label>
                {t('form.endDate')}
                <input type="date" value={form.endDate} onChange={(event) => setForm({ ...form, endDate: event.target.value })} />
                {errors.endDate && <span className="error-text">{t('validation.endDateRequired')}</span>}
              </label>
            </div>
            <div className="inline-fields">
              <label>
                {t('form.targetAmount')}
                <input type="number" min="0" value={form.targetAmount} onChange={(event) => setForm({ ...form, targetAmount: event.target.value })} />
                {errors.targetAmount && <span className="error-text">{t('validation.targetAmountPositive')}</span>}
              </label>
              <label>
                {t('form.achievedAmount')}
                <input type="number" min="0" value={form.achievedAmount} onChange={(event) => setForm({ ...form, achievedAmount: event.target.value })} />
              </label>
            </div>
            <label>
              {t('form.status')}
              <select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}>
                {statuses.map((status) => <option key={status} value={status}>{statusLabels[status]}</option>)}
              </select>
            </label>

            {/* Only on create - editing a goal uses the buttons on its card instead. */}
            {!editingId && (
              <div className="goal-attachments">
                <p className="goal-attachments-title">{t('attach.title')}</p>
                <p className="goal-attachments-intro">{t('attach.intro')}</p>

                <div className={`goal-attachment${attachments.withChecklist ? ' is-on' : ''}`}>
                  <label className="goal-attachment-toggle">
                    <input
                      type="checkbox"
                      checked={attachments.withChecklist}
                      onChange={(event) => setAttachments({ ...attachments, withChecklist: event.target.checked })}
                    />
                    <span className="goal-attachment-icon" aria-hidden="true">📅</span>
                    <span>
                      <strong>{t('attach.checklistTitle')}</strong>
                      <span className="field-hint">{t('attach.checklistHint')}</span>
                    </span>
                  </label>

                  {attachments.withChecklist && (
                    <div className="goal-attachment-body">
                      <div className="inline-fields">
                        <label>
                          {t('attach.repeat')}
                          <div className="segmented-toggle">
                            <button type="button" className={attachments.checklistReminderType === 'daily' ? 'active' : ''} onClick={() => setAttachments({ ...attachments, checklistReminderType: 'daily' })}>{t('attach.everyDay')}</button>
                            <button type="button" className={attachments.checklistReminderType !== 'daily' ? 'active' : ''} onClick={() => setAttachments({ ...attachments, checklistReminderType: 'once' })}>{t('attach.onceOnly')}</button>
                          </div>
                        </label>
                        <label>
                          {t('attach.remindAt')}
                          <input
                            type="time"
                            value={attachments.checklistReminderTime}
                            onChange={(event) => setAttachments({ ...attachments, checklistReminderTime: event.target.value })}
                          />
                          <span className="field-hint">{t('attach.remindAtHint')}</span>
                        </label>
                      </div>
                      <label>
                        {t('attach.tasks')}
                        <div className="task-editor">
                          {attachments.checklistTasks.map((task, index) => (
                            <div key={task.id} className="task-editor-row">
                              <input value={task.text} readOnly />
                              <button type="button" className="text-btn danger icon-btn" aria-label={t('attach.removeTask')} onClick={() => removeAttachmentTask(index)}>✕</button>
                            </div>
                          ))}
                          <div className="task-editor-row">
                            <input
                              placeholder={t('attach.taskPlaceholder')}
                              value={newTaskText}
                              onChange={(event) => setNewTaskText(event.target.value)}
                              onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addAttachmentTask(); } }}
                            />
                            <button type="button" className="text-btn" onClick={addAttachmentTask}>{t('attach.addTask')}</button>
                          </div>
                        </div>
                      </label>
                    </div>
                  )}
                </div>

                <div className={`goal-attachment${attachments.withSaving ? ' is-on' : ''}`}>
                  <label className="goal-attachment-toggle">
                    <input
                      type="checkbox"
                      checked={attachments.withSaving}
                      onChange={(event) => setAttachments({ ...attachments, withSaving: event.target.checked })}
                    />
                    <span className="goal-attachment-icon" aria-hidden="true">💰</span>
                    <span>
                      <strong>{t('attach.savingTitle')}</strong>
                      <span className="field-hint">{t('attach.savingHint')}</span>
                    </span>
                  </label>

                  {attachments.withSaving && (
                    <div className="goal-attachment-body">
                      <label>
                        {t('attach.savingAmount')}
                        <input
                          type="number"
                          min="0"
                          placeholder={form.targetAmount || '0'}
                          value={attachments.savingAmount}
                          onChange={(event) => setAttachments({ ...attachments, savingAmount: event.target.value })}
                        />
                        <span className="field-hint">{t('attach.savingAmountHint')}</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="modal-actions">
              <button type="button" className="text-btn" onClick={closeGoalForm}>{t('form.cancel')}</button>
              <button className="primary-btn standout-btn" type="submit">{editingId ? t('form.saveChanges') : t('form.addGoal')}</button>
            </div>
          </form>
        </div>
      )}

      {showProgressModal && (
        <div className="modal-overlay" onClick={closeProgressModal}>
          <form onSubmit={handleProgressSubmit} className="card form-card modal-content" onClick={(event) => event.stopPropagation()}>
            <h3>{t('progressModal.title')}</h3>
            {/* Where the goal stands right now, so the new number is an adjustment
                rather than a guess. */}
            <div className="progress-modal-context">
              <p className="progress-modal-title">{progressGoal.title}</p>
              <div className="progress-modal-figures">
                <span>{t('progressModal.current')}: {formatProgressAmount(progressGoal.achievedAmount)}</span>
                <span>{t('progressModal.target')}: {formatProgressAmount(progressGoal.targetAmount)}</span>
              </div>
              <div className="progress-bar"><div style={{ width: `${goalProgressPercent(progressGoal)}%` }} /></div>
            </div>
            <label>
              {t('progressModal.achievedAmount')}
              <input autoFocus type="number" min="0" value={progressValue} onChange={(event) => setProgressValue(event.target.value)} />
            </label>
            <div className="modal-actions">
              <button type="button" className="text-btn" onClick={closeProgressModal}>{t('progressModal.cancel')}</button>
              <button className="primary-btn standout-btn" type="submit">{t('progressModal.save')}</button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}

export default GoalDashboard;
